import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Icon, Input } from "@/ds";
import ImageUploader from "@/components/uploads/ImageUploader";
import { anonymousKeyFor, executeAction, rejectAction } from "@/lib/dany";

/**
 * The client's own profile, as a card in the conversation (DY2.3).
 *
 * DANY.md §2.3 asks that someone who arrived through the anonymous funnel end
 * up an active client without ever meeting a form. This is the last piece of
 * that: the fields nobody supplied — name, ZIP, city, state, photo — offered
 * once, inside the thread, on the same propose → confirm → execute contract the
 * job draft uses. The `action_id` is the whole authorization and the Save
 * button is the commit point; Dany cannot press it.
 *
 * **Which fields appear is the server's decision, not this component's.** The
 * card renders exactly the keys `tool_result.data.profile` carries, because
 * `execute_action` keeps only the keys that were proposed — so a field the tool
 * left out cannot be smuggled in from the browser. That is also why the photo
 * uploader is conditional on an `avatar_url` key rather than on a prop.
 *
 * **Nothing here blocks anything.** Ignoring the card, or dismissing it, leaves
 * the account exactly as it was; the offer is recorded either way, which is what
 * keeps Dany from raising it a second time.
 */
export default function DanyProfileWidget({ data, threadId, onCommitted }) {
  const { t } = useTranslation();
  const [fields, setFields] = React.useState(() => ({ ...(data?.profile || {}) }));
  const [busy, setBusy] = React.useState(false);
  const [phase, setPhase] = React.useState(data?.saved ? "saved" : "draft");
  const [error, setError] = React.useState(null);

  const actionId = data?.action_id;
  const has = (key) => Object.prototype.hasOwnProperty.call(fields, key);

  const empty = Object.entries(fields)
    .every(([, value]) => !String(value || "").trim());

  const save = async () => {
    if (!actionId) return;
    setBusy(true);
    setError(null);
    try {
      await executeAction(actionId, fields, {
        anonymousKey: anonymousKeyFor(threadId),
      });
      setPhase("saved");
      // The thread re-reads itself: this card is not the only thing on
      // screen that this press changed (T11 N38).
      onCommitted?.();
    } catch (err) {
      setError(
        t([`dany.profile.${err.code}`, `dany.errors.${err.code}`,
           "dany.profile.failed"], {
          defaultValue: t("dany.profile.failed"),
        })
      );
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await rejectAction(actionId, { anonymousKey: anonymousKeyFor(threadId) });
    } catch { /* the card goes away either way */ }
    setPhase("dismissed");
    // The thread re-reads itself: this card is not the only thing on
    // screen that this press changed (T11 N38).
    onCommitted?.();
    setBusy(false);
  };

  if (phase === "dismissed") return null;

  if (phase === "saved") {
    return (
      <Card variant="raised" padding="md" data-testid="dany-profile-saved">
        <p style={heading}>{t("dany.profile.savedTitle")}</p>
        <p style={body}>{t("dany.profile.saved")}</p>
      </Card>
    );
  }

  return (
    <Card variant="raised" padding="md" data-testid="dany-profile">
      <span style={eyebrow}>
        <Icon name="user" size={12} style={{ color: "var(--hive-ink)" }} />
        {t("dany.profile.header")}
      </span>
      <p style={body}>{t("dany.profile.body")}</p>

      {has("full_name") && (
        <Field label={t("dany.profile.fieldName")}>
          <Input
            value={fields.full_name || ""} maxLength={120}
            onChange={(e) => setFields({ ...fields, full_name: e.target.value })}
            data-testid="dany-profile-name"
          />
        </Field>
      )}
      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        {has("zip_code") && (
          <Field label={t("dany.profile.fieldZip")}>
            <Input
              value={fields.zip_code || ""} maxLength={5} inputMode="numeric"
              onChange={(e) => setFields({
                ...fields, zip_code: e.target.value.replace(/\D/g, "").slice(0, 5),
              })}
              data-testid="dany-profile-zip"
            />
          </Field>
        )}
        {has("city") && (
          <Field label={t("dany.profile.fieldCity")}>
            <Input
              value={fields.city || ""} maxLength={80}
              onChange={(e) => setFields({ ...fields, city: e.target.value })}
              data-testid="dany-profile-city"
            />
          </Field>
        )}
        {has("state") && (
          <Field label={t("dany.profile.fieldState")}>
            <Input
              value={fields.state || ""} maxLength={2} placeholder="FL"
              onChange={(e) => setFields({
                ...fields, state: e.target.value.toUpperCase(),
              })}
              data-testid="dany-profile-state"
            />
          </Field>
        )}
      </div>
      {has("avatar_url") && (
        <div style={{ marginTop: "var(--space-2)", maxWidth: 180 }}>
          <span style={fieldLabel}>{t("dany.profile.fieldPhoto")}</span>
          <ImageUploader
            value={fields.avatar_url || null}
            purpose="profile_photo"
            aspect="square"
            testid="dany-profile-photo"
            onUploaded={({ url }) => setFields({ ...fields, avatar_url: url })}
            onCleared={() => setFields({ ...fields, avatar_url: "" })}
          />
        </div>
      )}

      {error && (
        <p style={{ ...body, color: "var(--danger)" }}
           data-testid="dany-profile-error">{error}</p>
      )}

      <div style={{
        marginTop: "var(--space-4)", display: "flex", gap: "var(--space-2)",
        flexWrap: "wrap",
      }}>
        <Button variant="primary" size="sm" icon="check"
                disabled={busy || !actionId || empty} onClick={save}
                data-testid="dany-profile-save">
          {t("dany.profile.save")}
        </Button>
        <Button variant="ghost" size="sm" icon="x" disabled={busy}
                onClick={dismiss} data-testid="dany-profile-skip">
          {t("dany.profile.skip")}
        </Button>
      </div>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginTop: "var(--space-3)" }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

const fieldLabel = {
  display: "block", marginBottom: 4, fontFamily: "var(--font-display)",
  fontSize: "var(--text-xs)", textTransform: "uppercase",
  letterSpacing: "var(--tracking-widest)", color: "var(--dany-meta)",
};

const eyebrow = {
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "var(--font-display)", fontSize: "var(--text-xs)",
  textTransform: "uppercase", letterSpacing: "var(--tracking-widest)",
  color: "var(--dany-meta)",
};

const heading = {
  fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
  fontSize: "var(--text-lg)", color: "var(--text-body)",
};

const body = {
  marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
  color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)",
  maxWidth: "var(--measure-body)",
};
